import { useState, useEffect } from 'react';
import { MdClose, MdSchool, MdCalendarToday, MdAccessTime, MdTextFields, MdMic } from 'react-icons/md';
import { parentAPI } from '../../utils/api';

const X = MdClose as any;
const School = MdSchool as any;
const Calendar = MdCalendarToday as any;
const Clock = MdAccessTime as any;
const TextIcon = MdTextFields as any;
const MicIcon = MdMic as any;

interface Lesson {
  id: string;
  subject: string;
  course: string;
  topic: string | null;
  mode: 'text' | 'voice';
  started_at: string;
  ended_at: string | null;
  summary: string | null;
}

interface ViewHistoryModalProps {
  child: {
    id: string;
    name: string;
    email: string;
  };
  onClose: () => void;
}

export default function ViewHistoryModal({ child, onClose }: ViewHistoryModalProps) {
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLessons();
  }, [child.id]);

  const fetchLessons = async () => {
    try {
      setIsLoading(true);
      const response = await parentAPI.getChildLessons(child.id);
      setLessons(response.lessons);
    } catch (err: any) {
      console.error('Failed to fetch lessons:', err);
      setError(err.message || 'Failed to load lesson history');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const calculateDuration = (startedAt: string, endedAt: string | null) => {
    if (!endedAt) return 'In progress';

    const start = new Date(startedAt);
    const end = new Date(endedAt);
    const durationMs = end.getTime() - start.getTime();
    const minutes = Math.floor(durationMs / 60000);

    if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m`;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#111827] rounded-lg p-6 max-w-4xl w-full mx-4 border border-gray-600 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-white text-xl font-bold">Lesson History - {child.name}</h2>
            <p className="text-gray-400 text-sm">{child.email}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-white text-lg">Loading lessons...</div>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500 rounded p-4 text-red-500">
            {error}
          </div>
        ) : lessons.length === 0 ? (
          <div className="text-center py-12">
            <School className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-white text-lg font-semibold mb-2">No Lessons Yet</h3>
            <p className="text-gray-400">
              {child.name} hasn't started any lessons yet.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {lessons.map((lesson) => (
              <div
                key={lesson.id}
                className="bg-[#1a1f2e] rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-semibold text-lg">{lesson.subject}</h3>
                      {lesson.mode === 'voice' ? (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                          <MicIcon className="w-3 h-3" />
                          Voice
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs rounded">
                          <TextIcon className="w-3 h-3" />
                          Text
                        </span>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm">{lesson.course}</p>
                    {lesson.topic && (
                      <p className="text-gray-500 text-sm mt-1">Topic: {lesson.topic}</p>
                    )}
                  </div>
                </div>

                {lesson.summary && (
                  <div className="mb-3 p-3 bg-[#0f1419] rounded border border-gray-700">
                    <p className="text-gray-300 text-sm">{lesson.summary}</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(lesson.started_at)}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatTime(lesson.started_at)}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Duration: {calculateDuration(lesson.started_at, lesson.ended_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-transparent border border-gray-600 text-white rounded hover:bg-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
